import {Injectable} from '@angular/core';
import Decimal from 'decimal.js';
import {defer, MonoTypeOperatorFunction, Observable, of, pipe, timer} from 'rxjs';
import {map, mapTo, switchMap, take} from 'rxjs/operators';
import {correctNumber} from '../../common/formatters';
import {AccountType} from '../../enums/account-type';
import {AssetType, getOppositeAssetType} from '../../enums/asset-type';
import {OrdType} from '../../enums/order-type';
import {Side} from '../../enums/side';
import {Security} from '../../models/security';
import {formatSecurityKey, SecurityKey} from '../../models/security-key';
import {QuotesStreamingService} from '../../widgets/components/quotes-streaming';
import {Quote} from '../../widgets/components/quotes-streaming/quote';
import {UPDATES_GATING_PERIOD} from '../layout/widget/widget-updates-gating';
import {symbolDecimalsFixValueFormat, SymbolDecimalsInputParams, SymbolDecimalsService} from '../symbol-decimals';
import {getOrderQtyDecimalsInputParams} from './order-qty-decimals-input-params';
import {getOrderQtyMultiCurrencyInfo} from './order-qty-multi-currency';
import {OrderQtyDecimalsInputParams, OrderQtyMultiCurrencyInfo} from './order-qty-multi-currency-info';

const isOrderTypeWithLimitPrice = (orderType: OrdType): boolean =>
    orderType === OrdType.LIMIT
    || orderType === OrdType.STOP_LIMIT;

const isOrderTypeWithMarketPrice = (orderType: OrdType): boolean =>
    orderType === OrdType.MARKET;

@Injectable()
export class OrderQtyMultiCurrencyService {

    constructor(
        private quotesStreamingService: QuotesStreamingService,
        private symbolDecimalsService: SymbolDecimalsService
    ) {}

    public getOppositeCurrencyQtyStream(
        params: {
            security: Security;
            qtyAssetType: AssetType;
            qtyValue: number;
            limitPrice: number;
            orderType: OrdType;
            side: Side;
            accountType: AccountType;
        }
    ): Observable<number | undefined> {
        return defer(() => {
            const qtyMultiCurrencyInfo = getOrderQtyMultiCurrencyInfo(params.security, {
                orderType:   params.orderType,
                side:        params.side,
                accountType: params.accountType
            });

            return qtyMultiCurrencyInfo.isMultiCurrencyAllowed
                ? this.getInnerOppositeCurrencyQtyStream({...params, qtyMultiCurrencyInfo})
                : of(undefined);
        });
    }

    public resolveOrderQty(
        params: {
            security: Security;
            qtyAssetType: AssetType;
            qtyValue: number;
            limitPrice: number;
            orderType: OrdType;
            side: Side;
            accountType: AccountType;
            qtyMultiCurrencyInfo?: OrderQtyMultiCurrencyInfo
        }
    ): Observable<number | undefined> {
        return defer(() => {
            const qtyMultiCurrencyInfo = params.qtyMultiCurrencyInfo
                || getOrderQtyMultiCurrencyInfo(params.security, {
                    orderType:   params.orderType,
                    side:        params.side,
                    accountType: params.accountType
                });

            return (
                qtyMultiCurrencyInfo.isMultiCurrencyAllowed
                && !qtyMultiCurrencyInfo.isMultiCurrencyNativelySupported
                && qtyMultiCurrencyInfo.baseQtyAssetType !== params.qtyAssetType
            )
                ? this.getInnerOppositeCurrencyQtyStream({
                    ...params,
                    qtyMultiCurrencyInfo,
                    skipEmpty: true // Skip the first empty/dummy quote
                }).pipe(
                    take(1)
                )
                : of(params.qtyValue);
        });
    }

    private getOppositeCurrencyQty(
        params: {
            securityInputParams: SymbolDecimalsInputParams;
            qtyMultiCurrencyInfo: OrderQtyMultiCurrencyInfo;
            qtyAssetType: AssetType;
            qtyValue: number;
            price: number;
        }
    ): number | undefined {
        let oppositeQty: number | undefined;

        const isDefined = (value: number) =>
            value || value === 0;

        if (isDefined(params.qtyValue) && isDefined(params.price)) {
            const oppositeAssetType = getOppositeAssetType(params.qtyAssetType);

            oppositeQty = (oppositeAssetType === AssetType.BASE)
                ? new Decimal(params.qtyValue).div(params.price).toNumber()
                : new Decimal(params.qtyValue).mul(params.price).toNumber();

            const oppositeQtyInputParams = getOrderQtyDecimalsInputParams({
                securityInputParams:  params.securityInputParams,
                qtyAssetType:         oppositeAssetType,
                qtyMultiCurrencyInfo: params.qtyMultiCurrencyInfo
            });

            oppositeQty = this.correctQtyValue(oppositeQty, oppositeQtyInputParams);
        }

        return oppositeQty;
    }

    private correctQtyValue(
        rawQtyValue: number,
        qtyInputParams: OrderQtyDecimalsInputParams
    ): number {
        const DEFAULT_CORRECT_VALUE = 1;

        const isDefined = (value: number) =>
            value || value === 0;

        let correctQtyValue = symbolDecimalsFixValueFormat(
            {
                decimalPlaces: qtyInputParams.decimalCount,
                precision:     qtyInputParams.precision
            },
            rawQtyValue
        );

        correctQtyValue = correctNumber(
            correctQtyValue,
            isDefined(qtyInputParams.min) ? qtyInputParams.min : correctQtyValue,
            isDefined(qtyInputParams.max) ? qtyInputParams.max : correctQtyValue,
            qtyInputParams.step,
            isDefined(qtyInputParams.min) ? qtyInputParams.min : DEFAULT_CORRECT_VALUE
        );

        return correctQtyValue;
    }

    private getQuoteStream(securityKey: SecurityKey, skipEmpty: boolean): Observable<Quote> {
        const securityKeyStr = formatSecurityKey(securityKey);

        const isQuoteEmpty = (quote: Quote): boolean =>
            !(quote.bid || quote.ask);

        const quoteProcessing: MonoTypeOperatorFunction<Quote> = skipEmpty
            ? switchMap(quote =>
                isQuoteEmpty(quote)
                    ? timer(UPDATES_GATING_PERIOD).pipe(mapTo(quote))
                    : of(quote)
            )
            : pipe();

        return this.quotesStreamingService.createSubscription([securityKeyStr]).pipe(
            quoteProcessing
        );
    }

    private selectPrice(
        params: {
            orderType: OrdType;
            limitPrice: number;
            quote: Quote | undefined;
            side: Side;
        }
    ): number | undefined {
        const selectMarketPrice = (side: Side, quote: Quote): number =>
            side === Side.SELL
                ? quote.bid
                : quote.ask;

        return isOrderTypeWithLimitPrice(params.orderType)
            ? params.limitPrice
            : isOrderTypeWithMarketPrice(params.orderType)
                ? selectMarketPrice(params.side, params.quote)
                : undefined;
    }

    private getInnerOppositeCurrencyQtyStream(
        params: {
            security: Security;
            qtyMultiCurrencyInfo: OrderQtyMultiCurrencyInfo;
            qtyAssetType: AssetType;
            qtyValue: number;
            limitPrice: number;
            orderType: OrdType;
            side: Side;
            skipEmpty?: boolean;
        }
    ): Observable<number | undefined> {
        return defer(() => {
            const quote$: Observable<Quote> | Observable<undefined> = isOrderTypeWithMarketPrice(params.orderType)
                ? this.getQuoteStream(params.security, !!params.skipEmpty)
                : of(undefined);

            const securityInputParams = this.symbolDecimalsService.getSecurityInputParams(params.security);

            return quote$.pipe(
                map(quote => this.getOppositeCurrencyQty({
                    securityInputParams,
                    qtyMultiCurrencyInfo: params.qtyMultiCurrencyInfo,
                    price:                this.selectPrice({
                        side:       params.side,
                        orderType:  params.orderType,
                        limitPrice: params.limitPrice,
                        quote
                    }),
                    qtyAssetType:         params.qtyAssetType,
                    qtyValue:             params.qtyValue
                }))
            );
        });
    }

}
